import React, { FC, FormEvent, useCallback, RefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Icons } from '@tidbits/react-tidbits';

import NavigationBarLayout from '@ui-components/navigation-bar-layout';
import IconTemplate from '@ui-components/icon-template';
import UserPreferencesRedux from '@helper-hooks/user-preferences-redux-action';

import i18n from '../../i18n';

import NavigationBarMenuRight from './components/NavigationBarRight/NavigationBarMenuRight';
import { ROUTES } from '../../common/index';
import TRANSLATE_KEYS from '../../public/locales/translateKeys';
import getMatch from '../../store/match/matchSelector';
import { setSearchTermRequest } from '../../store/searchTerm/searchTermAction';
import { getSearchTerm } from '../../store/searchTerm/searchTermSelector';

import { getTaskWorkflowType } from '../../store/task/taskSelector';
import { ReduxState } from '../../common/types';
import history from '../../utils/history';
import { setSelectedTasks } from '../../store/insertTask/insertTaskActions';
import { getUserPreferences } from '../../store/userPreferences/userPreferencesSelector';
import { HeaderNavigationLeft, SearchInputField, SearchTooltip } from '../../ui-components';
import MediaTypeIcon from './components/MediaTypeIcon/MediaTypeIcon';
import EnabledFeature from '../../utils/FeaturesFlags/EnabledFeature';
import { FeatureFlagNames } from '../../store/featureFlags/featureFlagsReducer';

import NotificationsDropdown from './components/NotificationsDropdown/NotificationsDropdown';
import { ModalProvider, ModalRoot } from '@providers/modal-provider';

interface ComponentProps {
  searchInputRef: RefObject<HTMLDivElement>;
}

type Props = RouteComponentProps & ComponentProps;

const NavigationBar: FC<Props> = ({ searchInputRef }) => {
  const user = useSelector((state: ReduxState) => state.user);
  const searchTerm = useSelector(getSearchTerm);
  const matchFromStore = useSelector(getMatch);
  const workflowType = useSelector(getTaskWorkflowType);
  const currentUserPreferences = useSelector(getUserPreferences);
  const dispatch = useDispatch();

  const updateSearch = useCallback(
    (value: string) => {
      dispatch(setSearchTermRequest(value));
      dispatch(
        UserPreferencesRedux.userPreferencesAction.putUserPreferenceRequest('workflow', 'worklist', {
          searchTerm: value
        }, currentUserPreferences)
      );
    },
    [dispatch, currentUserPreferences]
  );

  const setSelectedTaskId = useCallback(
    (taskIds: number[]) => dispatch(setSelectedTasks(taskIds)),
    [dispatch]
  );

  const setSearchTerm = (event: FormEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value }
    } = event;
    updateSearch(value);
    setSelectedTaskId([]);
  };

  const handleHeaderLogoClick = () => {
    if (matchFromStore) {
      const { path } = matchFromStore;

      switch (path) {
        case ROUTES.moduleTaskDetails:
          history.push(ROUTES.worklist);
          break;
        default:
          history.push(ROUTES.worklist);
      }
    }
  };

  return (
    <ModalProvider>
      <NavigationBarLayout
        banner={
          <HeaderNavigationLeft
            mediaIcon={<MediaTypeIcon workflowType={workflowType} />}
            logoIcon={
              <IconTemplate
                name="headerNavBarIcon"
                onClick={handleHeaderLogoClick}
                width={50}
                height={50}
                pointer
              />
            }
          >
            <ModalRoot />
          </HeaderNavigationLeft>
        }
        user={
          <NavigationBarMenuRight
            isSearchVisible={false}
            searchNode={
              <>
                <SearchInputField
                  onInputChange={setSearchTerm}
                  searchValue={searchTerm}
                  iconName={Icons.SearchAltIcon}
                  placeholder={i18n.t(TRANSLATE_KEYS.navigation_bar.search_input_placeholder)}
                  ref={searchInputRef}
                />
                {
                  <div className={'mpe-navigation__search-helper'}>
                    <SearchTooltip />
                  </div>
                }
              </>
            }
            notificationNode={
              <EnabledFeature featureName={FeatureFlagNames.NOTIFICATIONS}>
                <NotificationsDropdown />
              </EnabledFeature>
            }
            userDisplayName={user.name}
            userJobTypes={user.jobTypes.join(',')}
          />
        }
      />
    </ModalProvider>
  );
};

export default withRouter(NavigationBar);
